import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import Button from '@/design_system/Button';
import FileCard from '@/design_system/FileCard';
import Menu from '@/design_system/Menu';
import { DangerMenuItem, MenuItem } from '@/design_system/Menu/Menu';
import { PhotoCardGrid } from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAfterSales from '@/icons/AfterSales.svg';
import IconBuilding from '@/icons/Building.svg';
import IconEdit from '@/icons/Edit.svg';
import IconMore from '@/icons/More.svg';
import IconPlace from '@/icons/Place.svg';
import IconStore from '@/icons/Store.svg';
import IconTrash from '@/icons/Trash.svg';
import IconWorkshop from '@/icons/Workshop.svg';
import IconWorld from '@/icons/World.svg';
import { GetArticleNameProps, useArticleName } from '@/models/article';
import { isImage, useDeleteMedium } from '@/models/medium';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useShipmentContext } from '@/routes/Shipments/Shipment/ShipmentContext';
import { useClientToken, useCurrentSession } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';
import { formatDate, fromNow } from '@/utils/date';
import useViewPort from '@/utils/useViewport';

import { IComment } from '../Comments/Comments';

import './Comment.css';

const { block, element } = createBEMClasses('comment');

export const Comment = ({
  comment,
  isReadOnly,
  displayArticleName,
  onCommentUpdated,
  onCommentDeleted,
  variant,
}: {
  comment: IComment;
  isReadOnly?: boolean;
  displayArticleName?: boolean;
  onCommentUpdated: (editedComment: string) => Promise<void>;
  onCommentDeleted: () => Promise<void>;
  variant?: 'default' | 'client-communication';
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const clientToken = useClientToken();
  const [isEditing, setIsEditing] = useState(false);
  const hasBeenEdited = comment.updatedAt > comment.createdAt;

  const creatorCommentLabel = comment.creator?.name;
  const clientCreatorCommentLabel = clientToken ? (
    <Trans id="comment.creator.label.you">You</Trans>
  ) : 'clientCreator' in comment && comment.clientCreator?.name ? (
    comment.clientCreator.name
  ) : (
    <Trans id="comment.creator.label.the-client">The client</Trans>
  );

  return (
    <div className={block()}>
      <Stack row gap="0.75rem" justifyContent="space-between" flexWrap="nowrap">
        {!isMobile && !!comment.creator && <UserAvatar user={comment.creator} />}
        <div
          className={element('content', { 'client-comment': !!comment.clientCreatorId })}
          style={{ minWidth: 0 }}
        >
          <Stack
            row
            gap="0.25rem"
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="nowrap"
          >
            <Stack row alignItems="baseline" gap="0.25rem">
              <Paragraph weight="medium">
                {comment.creator ? (
                  creatorCommentLabel
                ) : comment.clientCreatorId ? (
                  clientCreatorCommentLabel
                ) : (
                  <Trans id="comment.creator.label.automatic">Automatic comment</Trans>
                )}
              </Paragraph>
              {displayArticleName && comment.article && <ArticleName article={comment.article} />}
            </Stack>
            <Stack
              row
              alignItems="center"
              gap="0.5rem"
              flexWrap="nowrap"
              style={{ height: '1.5rem' }}
            >
              <Stack row alignItems="center" gap="0.25rem" flexWrap="nowrap">
                <Paragraph size="200" color="disabled" nowrap>
                  <small
                    title={formatDate(new Date(comment.createdAt), {
                      dateStyle: 'long',
                      timeStyle: 'short',
                    })}
                  >
                    {fromNow(new Date(comment.createdAt))}
                  </small>
                </Paragraph>
                {hasBeenEdited && !isEditing && (
                  <HasBeenEdited updatedAtDate={new Date(comment.updatedAt)} />
                )}
                <InlineText size="200" color="disabled">
                  <small>•</small>
                </InlineText>
                <Visibility comment={comment} />
              </Stack>

              {!isReadOnly && !isEditing && (
                <Tooltip
                  content={t({ id: 'comment.viewer.other-options', message: 'Other options' })}
                  hideOnTouchDevice
                >
                  <Menu
                    trigger={
                      <Button
                        iconOnly={true}
                        size="small"
                        variant="secondary"
                        ariaLabel={t({
                          id: 'comment.viewer.other-options',
                          message: 'Other options',
                        })}
                      >
                        <IconMore />
                      </Button>
                    }
                    placement="bottom right"
                    onAction={(action: string) => {
                      if (action === 'edit') {
                        setIsEditing(true);
                      } else if (action === 'delete') {
                        onCommentDeleted();
                      }
                    }}
                  >
                    <MenuItem id="edit">
                      <Stack row gap="0.5rem" alignItems="center">
                        <IconEdit />
                        <Trans id="comment.viewer.edit">Edit</Trans>
                      </Stack>
                    </MenuItem>
                    <DangerMenuItem id="delete">
                      <Stack row gap="0.5rem" alignItems="center">
                        <IconTrash />
                        <Trans id="comment.viewer.delete">Delete</Trans>
                      </Stack>
                    </DangerMenuItem>
                  </Menu>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          {!isEditing ? (
            <CommentViewer comment={comment} variant={variant} />
          ) : (
            <CommentEditor
              initialContent={comment.content}
              onSaveComment={async (editedComment) => {
                await onCommentUpdated(editedComment);
                setIsEditing(false);
              }}
              onCancelEdition={() => setIsEditing(false)}
            ></CommentEditor>
          )}
        </div>
      </Stack>
    </div>
  );
};

const ArticleName = ({ article }: GetArticleNameProps) => {
  const articleName = useArticleName({ article });

  return (
    <InlineText size="200">
      <small> • </small>
      <small>
        <Trans id="comment.viewer.from">From</Trans>
      </small>{' '}
      <small>
        <InlineText size="200" weight="medium">
          {articleName}
        </InlineText>
      </small>
    </InlineText>
  );
};

/**
 * Simple comment viewer, just displays the content and the media
 * @param content The comment content
 * @constructor
 */
const CommentViewer = ({
  comment,
  variant,
}: {
  comment: IComment;
  variant?: 'default' | 'client-communication';
}) => {
  const images = comment.media.filter((medium) => isImage(medium));
  const otherFiles = comment.media.filter((medium) => !isImage(medium));
  const { currentSession } = useCurrentSession();
  const clientToken = useClientToken();

  const { mutateAsync: deleteMedium } = useDeleteMedium();
  const canDeleteMedia = comment.creatorId
    ? comment.creatorId === currentSession?.id
    : !!clientToken;

  return (
    <Stack gap="0.5rem">
      <Paragraph whiteSpace="pre-wrap" wordBreak="break-word">
        {comment.content}
      </Paragraph>
      {images.length > 0 && (
        <PhotoCardGrid
          media={images}
          size={variant === 'client-communication' ? 'xxlarge' : 'xlarge'}
          imgStyle={{ objectFit: 'cover' }}
          onDelete={
            canDeleteMedia
              ? (id) => {
                  deleteMedium(id);
                }
              : undefined
          }
        />
      )}
      {otherFiles.map((medium) => (
        <FileCard
          key={medium.id}
          name={medium.originalPath ?? ''}
          url={medium.url}
          size={medium.size}
          style={{ backgroundColor: 'var(--color-neutral-0)' }}
          showDeletedPlaceholder={medium.deletedAt !== null}
          onDelete={
            canDeleteMedia
              ? () => {
                  deleteMedium(medium.id);
                }
              : undefined
          }
        />
      ))}
    </Stack>
  );
};

/**
 * Comment editor, allows to edit the comment content
 * @param initialContent The initial comment content
 * @param onCancelEdition Callback when the user cancels the edition
 * @param onSaveComment Callback when the user saves the comment, contains the edited comment
 * @constructor
 */
const CommentEditor = ({
  initialContent,
  onCancelEdition,
  onSaveComment,
}: {
  initialContent: string;
  onCancelEdition: () => void;
  onSaveComment: (editedComment: string) => Promise<void>;
}) => {
  const [content, setContent] = useState(initialContent);
  const { t } = useLingui();

  return (
    <>
      <TextArea
        ariaLabel={t({
          id: 'comment.editor.input',
          message: 'Edit the comment',
        })}
        value={content ?? ''}
        rows={4}
        onChange={(evt) => setContent(evt.target.value)}
      />
      <Stack row alignItems="center" justifyContent="flex-end" gap="0.5rem">
        <Button
          size="small"
          variant="secondary"
          onPress={() => onCancelEdition()}
          ariaLabel={t({
            id: 'comment.editor.cancel',
            message: 'Cancel',
          })}
        >
          <Trans id="comment.editor.cancel">Cancel</Trans>
        </Button>
        <Button
          variant="primary"
          size="small"
          disabled={content === initialContent}
          onPress={() => onSaveComment(content)}
          ariaLabel={t({ id: 'comment.editor.save', message: 'Save changes' })}
        >
          <Trans id="comment.editor.save">Save changes</Trans>
        </Button>
      </Stack>
    </>
  );
};

const Visibility = ({ comment }: { comment: IComment }) => {
  const { t } = useLingui();
  const { request } = useRequestContext();
  const { shipment } = useShipmentContext();

  const organization = request?.organization ?? shipment?.organization;

  let label;
  let icon;

  if (comment.visibility === 'public') {
    label = t({
      id: 'comment.viewer.visibility.public',
      message: 'Visible by everyone with access to this page',
    });
    icon = <IconWorld />;
  } else if (comment.visibility === 'organization') {
    label = t({
      id: 'comment.viewer.visibility.organization',
      message: `Only ${organization?.name} users can see this comment`,
    });
    icon = <IconBuilding />;
  } else if (comment.visibility === 'entity') {
    label = t({
      id: 'comment.viewer.visibility.entity',
      message: `Only ${comment.entity?.name} users can see this comment`,
    });
    icon =
      request?.storeId === comment.entityId ? (
        <IconStore />
      ) : request?.afterSalesId === comment.entityId ? (
        <IconAfterSales />
      ) : shipment?.originEntityId === comment.entityId ? (
        <IconPlace start />
      ) : shipment?.destinationEntityId === comment.entityId ? (
        <IconPlace end />
      ) : (
        <IconWorkshop />
      );
  }

  return (
    <Button ariaLabel={label} tooltip={label} variant="style-less">
      {icon}
    </Button>
  );
};

const HasBeenEdited = ({ updatedAtDate }: { updatedAtDate: Date }) => (
  <InlineText
    size="200"
    color="disabled"
    nowrap
    title={formatDate(updatedAtDate, {
      dateStyle: 'long',
      timeStyle: 'short',
    })}
  >
    <small>
      <Trans id="comment.viewer.edition-flag">(edited)</Trans>
    </small>
  </InlineText>
);
